import FeedbackTable from "@pages/ItemDetails/parts/CardOpenEndedFeedback/FeedbackTable";
import React, { FC, memo, useCallback, useMemo } from "react";
import { iTableConfigItem } from "@components/Table/TableConfig";
import { OpenEnd, Topic } from "@pages/ItemDetails/parts/CardOpenEndedFeedback/LikesAndDislikes";

const TopicsAndOpenEnds: FC<{
    topics: Topic[];
    openEnds: OpenEnd[];
    isLike?: boolean;
}> = ({
    topics,
    openEnds: openEndsRaw,
    isLike = false,
}) => {
    const [activeTopicsIndex, setActiveTopicsIndex] = React.useState<number | null>(null);

    const onTopicClick = useCallback(
        (index: number) => {
            setActiveTopicsIndex(index !== activeTopicsIndex ? index : null);
        },
        [activeTopicsIndex]
    );

    const activeTopic = useMemo(
        (): Topic | null => activeTopicsIndex !== null ? topics[activeTopicsIndex] : null,
        [activeTopicsIndex, topics]
    );

    const openEnds = useMemo(
        () => {
            if (!openEndsRaw) return [];
            if (!activeTopic) return openEndsRaw;

            return openEndsRaw.filter(i => {
                return activeTopic.openEnds?.includes(i.id ?? 0);
            });
        },
        [openEndsRaw, activeTopic]
    );

    const topicsTableConfig = useMemo(
        (): iTableConfigItem[] => [
            {
                key: 'name',
                title: (
                    <div className={`CardOpenEndedFeedback__table-header is-${isLike ? 'like' : 'dislike'}`}>
                        TOP {isLike ? 'LIKES' : 'DISLIKES'}
                    </div>
                ),
                Value: ({ index }) => (<>
                    {topics[index].topicName}
                </>),
            },
            {
                key: 'percentage',
                title: '%',
                Value: ({ index }) => (<>
                    {topics[index].relativeValue ?? 0}%
                </>),
                styles: {
                    cell: {
                        flex: `0 0 40px`,
                        justifyContent: 'center',
                    }
                }
            },
            {
                key: 'count',
                title: '#',
                Value: ({ index }) => (<>
                    {topics[index].count}
                </>),
                styles: {
                    cell: {
                        flex: `0 0 40px`,
                        justifyContent: 'center',
                    }
                }
            },
        ],
        [isLike, topics]
    );

    const openEndsTableConfig = useMemo(
        (): iTableConfigItem[] => [
            {
                key: 'text',
                title: (
                    <div className={`CardOpenEndedFeedback__table-header is-${isLike ? 'like' : 'dislike'}`}>
                        IN THEIR OWN WORDS {activeTopic ? (
                        <div className="CardOpenEndedFeedback__table-header-sub">
                            ({activeTopic.topicName})
                        </div>
                    ) : null}
                    </div>
                ),
                Value: ({ index }) => (
                    <div className="CardOpenEndedFeedback__open-end-text">
                        {openEnds[index].text}
                    </div>
                )
            },
        ],
        [isLike, openEnds, activeTopic]
    );

    return (<>
        <div className="CardOpenEndedFeedback__column">
            <FeedbackTable
                config={topicsTableConfig}
                onRowClick={onTopicClick}
                itemsCount={(topics || []).length}
                activeRowsIndexes={activeTopicsIndex !== null ? [activeTopicsIndex] : []}
            />
        </div>
        <div className="CardOpenEndedFeedback__column">
            <FeedbackTable
                config={openEndsTableConfig}
                itemsCount={openEnds.length}
                rowHeight="auto"
            />
        </div>
    </>);
};

export default memo(TopicsAndOpenEnds);